import React from 'react';
import { UserDropdownDto } from '../../types';

// Helpers for @tagging users in project messages.

/**
 * Finds the "@query" being typed just before the caret. An "@" starts a tag only at the
 * start of the text or after whitespace. Returns null when the caret is not inside a tag.
 */
export function getActiveMention(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(before[at - 1])) return null;

  const query = before.slice(at + 1);
  if (query.length > 40 || /^\s/.test(query) || /[\n@]/.test(query)) return null;
  return { start: at, query };
}

/** Tagged users whose "@Display Name" is still in the text — deleting the text removes the tag. */
export function taggedUsersInText(text: string, tagged: UserDropdownDto[]): UserDropdownDto[] {
  const lower = text.toLowerCase();
  return tagged.filter((u) => lower.includes(`@${u.displayName}`.toLowerCase()));
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const tagStyle: React.CSSProperties = {
  fontWeight: 600,
  color: '#1d4ed8',
  backgroundColor: '#dbeafe',
  borderRadius: '3px',
  padding: '0 3px',
};

/** Renders message text with each "@Name" of a tagged user highlighted. */
export function renderMessageWithTags(content: string, taggedUsers?: UserDropdownDto[]): React.ReactNode {
  const names = (taggedUsers ?? [])
    .map((u) => u.displayName)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length); // longest first so "Ann Lee" wins over "Ann"
  if (names.length === 0) return content;

  const pattern = new RegExp(`(@(?:${names.map(escapeRegExp).join('|')}))`, 'gi');
  return content.split(pattern).map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} data-testid="message-tag" style={tagStyle}>
        {part}
      </span>
    ) : (
      part
    ),
  );
}
